import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { TitleContinuityQualification, formatOwnershipBranch } from './TitleContinuityQualification';

const SummaryStrip = ({ analysis }) => {
  const values = [
    ['Referenced documents', (analysis.referenced_documents || []).length],
    ['Uploaded documents', (analysis.available_docs || []).length],
    ['Confirmed missing', (analysis.missing_documents || []).length],
    ['Verification required', (analysis.verification_required || []).length],
  ];
  return <div className="grid grid-cols-2 gap-px border bg-slate-200 sm:grid-cols-4" data-testid="saved-report-summary-strip">
    {values.map(([label, value]) => <div className="bg-white p-4" key={label}>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-950" data-testid={`saved-report-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
    </div>)}
  </div>;
};

const ReportTable = ({ title, rows, columns, emptyText, testId }) => <section className="mt-6" data-testid={testId}>
  <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
  {!rows?.length ? <p className="mt-2 text-sm text-slate-500" data-testid={`${testId}-empty`}>{emptyText}</p> : <div className="mt-3 overflow-x-auto border">
    <table className="min-w-full text-left text-sm">
      <thead className="bg-slate-900 text-xs uppercase text-white"><tr>{columns.map((column) => <th className="px-3 py-3 font-semibold" key={column.label}>{column.label}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-200 bg-white">
        {rows.map((row, index) => <tr key={`${testId}-${index}`}>{columns.map((column) => <td className="px-3 py-3 align-top text-slate-700" data-testid={`${testId}-${column.label.toLowerCase().replace(/\s+/g, '-')}-${index}`} key={column.label}>{column.value(row, index) || '—'}</td>)}</tr>)}
      </tbody>
    </table>
  </div>}
</section>;

const TitleContinuity = ({ title }) => {
  if (!title) return null;
  const timeline = title.deed_tree || title.chronology || [];
  return <section className="mt-8 border-t pt-8" data-testid="saved-report-title-continuity">
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <h2 className="text-xl font-semibold text-slate-950">Title continuity</h2>
      <span className="text-sm font-semibold text-slate-700" data-testid="saved-report-title-status">{String(title.status || 'Not recorded').replace(/_/g, ' ')}</span>
    </div>
    <p className="mt-2 text-sm text-slate-600">{title.summary || 'No title continuity summary was recorded.'}</p>
    <div className="mt-5"><TitleContinuityQualification title={title} testIdPrefix="saved-report-title" /></div>
    <ReportTable
      title="Deed-based title chain"
      rows={timeline}
      testId="saved-report-title-timeline"
      emptyText="No title chronology was recorded."
      columns={[
        { label: 'Year', value: (entry) => entry.year || entry.date_display || entry.date },
        { label: 'Deed Type', value: (entry) => entry.deed_type || entry.doc_type_label || entry.doc_type },
        { label: 'Ownership Branch', value: (entry) => formatOwnershipBranch(entry.branch_id || entry.trunk_id) },
        { label: 'From', value: (entry) => entry.from_owner || entry.from },
        { label: 'To', value: (entry) => entry.to_owner || entry.to },
        { label: 'Status', value: (entry) => String(entry.validation_status || entry.link_status || '').replace(/_/g, ' ') },
      ]}
    />
  </section>;
};

export const MissingDocumentsReportViewer = ({ data, report, onBack }) => {
  const resolvedReport = report || data?.report || data || {};
  const analysis = resolvedReport?.analysis_result || resolvedReport || {};
  const referenced = analysis.referenced_documents || [];
  const uploaded = analysis.available_docs || [];
  const missing = analysis.missing_documents || [];
  const verification = analysis.verification_required || [];
  return <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="saved-report-viewer">
    <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-950" data-testid="saved-report-back-button"><ArrowLeft size={16} /> Back to reports</button>
    <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
      <div><p className="text-sm font-semibold uppercase text-slate-500">Saved report</p><h1 className="mt-1 text-3xl font-semibold text-slate-950">Missing Documents Identifier</h1></div>
      <p className="text-sm text-slate-600" data-testid="saved-report-status">{String(resolvedReport?.status || analysis.status || 'Completed').replace(/_/g, ' ')}</p>
    </div>
    <div className="mt-6"><SummaryStrip analysis={analysis} /></div>
    <ReportTable title="Referenced Documents" rows={referenced} testId="saved-report-referenced" emptyText="No referenced documents were recorded." columns={[
      { label: 'Document', value: (row) => row.doc_type_label || row.doc_type },
      { label: 'Number', value: (row) => row.doc_no },
      { label: 'Source deed', value: (row) => row.source_deed || row.referenced_in },
      { label: 'Evidence status', value: (row) => row.reference_disposition || row.status },
    ]} />
    <ReportTable title="Uploaded Documents" rows={uploaded} testId="saved-report-uploaded" emptyText="No uploaded documents were recorded." columns={[
      { label: 'Document', value: (row) => row.doc_type_label || row.doc_type },
      { label: 'Number', value: (row) => row.doc_no || row.document_number },
      { label: 'File', value: (row) => row.file_name },
    ]} />
    <ReportTable title="Missing Documents" rows={missing} testId="saved-report-missing" emptyText="No confirmed missing documents were recorded." columns={[
      { label: 'Document', value: (row) => row.doc_type_label || row.doc_type },
      { label: 'Number', value: (row) => row.doc_no },
      { label: 'Reason', value: (row) => row.reason || row.narration },
    ]} />
    <ReportTable title="Verification Required" rows={verification} testId="saved-report-verification" emptyText="No verification items were recorded." columns={[
      { label: 'Document', value: (row) => row.doc_type_label || row.doc_type },
      { label: 'Number', value: (row) => row.doc_no },
      { label: 'Reason', value: (row) => row.reason || row.match_reason || row.mismatch_reason },
    ]} />
    <TitleContinuity title={analysis.title_continuity} />
  </main>;
};